import { useMemo, useCallback, useRef, useState } from "react"
import PropTypes from "prop-types"
import { map, chain, reduce, size, flatMap, find, isFunction, orderBy, noop } from "lodash"
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material"
import classNames from "classnames"
import pluralize from "pluralize"
import { handleGenerateHeaders, handleGenerateRows } from "connected-components/dashboard/components/download/helpers"
import { combineStatuses, ERROR, LOADING, OK } from "helpers/data-statuses"
import { useOutletContext } from "react-router-dom"
import singleDateContract from "gatherers/single-date/contract"
import { useDownloadSubscribers } from "hooks/register-downloads"
import useResizeObserver from "hooks/resize-observer"
import CompareTableStatus from "../compare-section/loading"
import useDownloadData from "../../hooks/download-data"
import EllipsableItem from "./ellipsable-item"
import { GroupSelector, ToggleExpanded, ToggleAll, ToggleDisplayMode, MODES } from "./section-controls"
import { TopXAccordionDetails, TableAccordionDetails } from "./accordion-details"
import SkeletonSection from "../skeleton-section"

import "./style.scss"

const MAX_ROWS = 10

export default function CompareTable(props) {
  const { setDownloadData = noop } = useOutletContext()
  const {
    status: dataStatus,
    classificationsStatus,
    data,
    name,
    configKey,
    formatter,
    valueGetter,
    labelFormatter,
    color,
    defaultExpanded = false,
    columnCount,
    rowCount = MAX_ROWS,
    availableGroups,
    selectedGroup,
    setSelectedGroup,
    positionSetsMetadata,
    instrumentsWithMetadata,
    downloadName,
    orderRowsBy,
    index: sectionIndex,
    queryState = { expanded: defaultExpanded, all: false, mode: MODES.TOP_X },
    onQueryStateChange = noop,
    isEndVsStartDateComparison,
    useSkeletonLoader = false,
    buttonSize = "medium",
  } = props

  const status = useMemo(() => combineStatuses([dataStatus, classificationsStatus]), [dataStatus, classificationsStatus])

  const accordionDetailsRef = useRef(null)
  const [hasScrollbar, setHasScrollbar] = useState(false)

  // The column format makes rendering the top X items easier but it could probably converted
  // to the rows data structure and avoid having 2 different AccordionDetails renderers
  const columns = useMemo(() => reduce(data, (result, item, index) => {
    result[index] = orderBy(item.values, (value) => Math.abs(valueGetter(value)), "desc")
    return result
  }, {}),
  [data, valueGetter])

  const rows = useMemo(() => {
    const flatValues = flatMap(data, item => item.values)
    // Get all unique row IDs
    const uniqueIds = chain(flatValues)
      .map(item => item.id)
      .uniq()
      .value()

    // Create row objects
    const processedRows = uniqueIds.map(id => {
      const itemWithDescription = find(flatValues, item => item.id === id)

      const values = data.map(({ values }) =>
        valueGetter(values.find(item => item.id === id)),
      )

      return {
        id,
        ...itemWithDescription,
        values,
      }
    })

    // Apply custom ordering if provided
    return isFunction(orderRowsBy)
      ? orderRowsBy(processedRows)
      : processedRows
  }, [data, valueGetter, orderRowsBy])

  const summary = useMemo(() => {
    let groupLabel = selectedGroup?.label || name
    if (groupLabel && !groupLabel.includes("/")) {
      groupLabel = pluralize(groupLabel)
    }
    return map(columns, (columnRows) => {
      return {
        id: "topX",
        label: `Top ${rowCount} ${groupLabel}`,
        value: chain(columnRows)
          .take(rowCount)
          .filter((item) => !item.notSummable)
          .sumBy((item) => Math.abs(valueGetter(item)))
          .value(),
      }
    })
  }, [selectedGroup?.label, name, columns, rowCount, valueGetter])

  const generateHeaders = useCallback(() =>
    handleGenerateHeaders(instrumentsWithMetadata, positionSetsMetadata, "ID", isEndVsStartDateComparison),
  [instrumentsWithMetadata, positionSetsMetadata, isEndVsStartDateComparison])

  const generateRows = useCallback(() =>
    handleGenerateRows(rows, formatter),
  [rows, formatter])

  useDownloadData({
    configKey,
    sectionIndex,
    name: downloadName || name,
    setDownloadData,
    generateHeaders,
    generateRows,
  })

  useDownloadSubscribers(configKey, () => ({
    combineSubscribers: true,
    name: (downloadName || name).replace("/", "-"),
    data: { rows: [...generateRows()], headers: generateHeaders() },
  }))

  // Check if the AccordionDetails has a scrollbar by comparing scroll dimensions
  useResizeObserver(accordionDetailsRef, useCallback(() => {
    if (accordionDetailsRef.current) {
      const element = accordionDetailsRef.current
      // Check if content height exceeds visible height
      const hasVerticalScrollbar = element.scrollHeight > element.clientHeight
      setHasScrollbar(hasVerticalScrollbar)
    }
  }, []))

  if (status === LOADING) {
    if (useSkeletonLoader) {
      return <SkeletonSection rowCount={rowCount} colCount={columnCount} />
    }
    return <CompareTableStatus {...{
      status: "Loading...",
      expanded: queryState?.expanded,
      color,
      name,
      columnCount,
      rowCount,
    }}
    />
  }

  if (status === ERROR) {
    return <div>Error</div>
  }

  function toggleExpanded(e) {
    e.stopPropagation()
    const newExpanded = !queryState?.expanded
    onQueryStateChange({
      ...queryState,
      expanded: newExpanded,
      ...(!newExpanded && { all: false }),
    })
  }

  function toggleAll(e) {
    e.stopPropagation()
    onQueryStateChange({ ...queryState, all: !queryState?.all })
    return false
  }

  function toggleDisplayMode(e) {
    e.stopPropagation()
    onQueryStateChange({ ...queryState, mode: queryState?.mode === MODES.TABLE ? MODES.TOP_X : MODES.TABLE })
    return false
  }

  const showAllButton = queryState?.mode === MODES.TOP_X ? reduce(columns, (acc, column) => column.length > acc ? column.length : acc, 0) > rowCount : size(rows) > rowCount

  return <Accordion square expanded={queryState?.expanded} className={classNames("renderers_compare-table", { all: queryState?.all })} data-test-class="compare-table">
    <AccordionSummary className="header" component="div">
      <div style={{ backgroundColor: color }} />
      <div className="section-controls" data-test-class={name}>
        <GroupSelector name={name} availableGroups={availableGroups} selectedGroup={selectedGroup} setSelectedGroup={setSelectedGroup} buttonSize={buttonSize} />
        <ToggleExpanded expanded={queryState?.expanded} toggleExpanded={toggleExpanded} buttonSize={buttonSize} />
        <ToggleAll expanded={queryState?.expanded} all={queryState?.all} showAllButton={showAllButton} toggleAll={toggleAll} buttonSize={buttonSize} />
        <ToggleDisplayMode mode={queryState?.mode} toggleDisplayMode={toggleDisplayMode} buttonSize={buttonSize} />
      </div>
      {map(summary, ({ value, label }, i) => queryState?.mode === MODES.TOP_X
        ? <EllipsableItem key={i} label={label} value={formatter(value)} barWidth={Math.abs(value)} />
        : <div key={i} />,
      )}
    </AccordionSummary>
    <AccordionDetails ref={accordionDetailsRef} className={classNames("body", queryState?.mode, { "has-scrollbar": hasScrollbar })}>
      {queryState?.mode === MODES.TOP_X
        ? <TopXAccordionDetails {...{
          columns,
          labelFormatter,
          valueGetter,
          formatter,
          color,
          all: queryState?.all,
          rowCount,
        }}
        />
        : <TableAccordionDetails {...{
          rows,
          labelFormatter,
          valueGetter,
          formatter,
          color,
        }}
        />}
    </AccordionDetails>
  </Accordion>
}

CompareTable.propTypes = {
  configKey: PropTypes.string.isRequired,
  status: PropTypes.oneOf([OK, ERROR, LOADING]).isRequired,
  classificationsStatus: PropTypes.oneOf([OK, ERROR, LOADING]).isRequired,
  name: PropTypes.string.isRequired,
  data: PropTypes.arrayOf(singleDateContract),
  formatter: PropTypes.func.isRequired,
  valueGetter: PropTypes.func.isRequired,
  labelFormatter: PropTypes.func.isRequired,
  color: PropTypes.string,
  defaultExpanded: PropTypes.bool,
  columnCount: PropTypes.number,
  rowCount: PropTypes.number,
  availableGroups: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    isClassification: PropTypes.bool,
    options: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      isClassification: PropTypes.bool,
      classificationId: PropTypes.string,
    })),
  })),
  selectedGroup: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  }),
  setSelectedGroup: PropTypes.func,
  mode: PropTypes.oneOf(Object.values(MODES)),
  expanded: PropTypes.bool,
  all: PropTypes.bool,
  positionSetsMetadata: PropTypes.shape({
    name: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
  }).isRequired,
  instrumentsWithMetadata: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  })).isRequired,
  downloadName: PropTypes.string,
  orderRowsBy: PropTypes.func,
  index: PropTypes.number.isRequired,
  queryState: PropTypes.shape({
    expanded: PropTypes.bool,
    all: PropTypes.bool,
    mode: PropTypes.oneOf(Object.values(MODES)),
  }),
  onQueryStateChange: PropTypes.func,
  isEndVsStartDateComparison: PropTypes.bool,
  useSkeletonLoader: PropTypes.bool,
  buttonSize: PropTypes.string,
}
